import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../constants/common'
import { QA_NAME_CANCEL_BTN } from '../../../constants/qa'
import {
  useInlineNotifications,
  useSelection,
  useValidationWithoutFormikNotifications,
} from '../../../hooks'
import { isFolderFile } from '../../../utils/file'
import { useFilePreviewGallery } from '../../FilesPreviewGallery'
import { TableRowActions } from '../../Table'
import { TableModal, useTableModalCloseHandler } from '../../TableModal'
import { useFileTableLoader } from '../File.hooks'
import { FileAttachedItemsModalTableRow } from '../FileAttachedItemsModalTableRow'
import { FileDirectoryBreadcrumbs } from '../FileDirectoryBreadcrumbs'

import { Actions } from './Actions'
import {
  ATTACH_FILES_COLUMNS_CONFIG,
  QA_NAME_FILE_ATTACH_MODAL,
} from './FileAttachItemsModal.constants'
import {
  getAttachItemsApiParams,
  isEqualFileAttachItemsModal,
  parseAttachItemPaginationData,
} from './FileAttachItemsModal.utils'

import FileIcon from '../../../icons/file.svg'

function FileAttachItemsModal({
  entityId,
  getApiProps = getAttachItemsApiParams,
  getDataPagination = parseAttachItemPaginationData,
  headerProps,
  initialValues,
  objectId,
  onClose,
  onSubmit,
  ...restProps
}) {
  const { t } = useTranslation()

  const [isSubmitting, setSubmitting] = useState(false)

  const { handlePrependNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const {
    breadcrumbsItems,
    count,
    dataOrdering,
    files,
    handleClickFolder,
    handleSetOrdering,
    isInitLoading,
    loading,
    pagination,
    setPagination,
  } = useFileTableLoader({
    ...restProps,
    getApiProps,
    getDataPagination,
    objectId: objectId || entityId,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({
      files,
    })

  const {
    checkIsItemSelected,
    exclude,
    handleInitializeSelection,
    handleSelect,
    handleSelectAll,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
  } = useSelection({
    data: { items: files?.filter(i => !isFolderFile(i)) },
    pagination,
  })

  const { isValidForm, setNotificationListRef } =
    useValidationWithoutFormikNotifications({
      validationSchemaProps: {
        isEqual: isEqualFileAttachItemsModal,
        isEqualProps: { selected },
      },
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handlePrependNotification,
    })

  useEffect(() => {
    if (initialValues) {
      handleInitializeSelection(initialValues)
    }
  }, [])

  function handleSubmit() {
    if (!isValidForm()) {
      return
    }

    setSubmitting(true)

    onSubmit(selected)
    onClose()
  }

  const { handleCloseTableModal, isCloseConfirm, setCloseConfirm } =
    useTableModalCloseHandler({
      initialSelectionValues: EMPTY_LIST,
      selected,
      onClose,
    })

  return (
    <TableModal
      initialFullScreen
      Actions={Actions}
      Counter={FileDirectoryBreadcrumbs}
      Search={null}
      actionsProps={{
        buttonAttachProps: {
          disabled: !selected,
          isLoading: isSubmitting,
          onClick: handleSubmit,
        },
        buttonCancelProps: {
          qaName: QA_NAME_CANCEL_BTN,
          onClick: handleCloseTableModal,
        },
        ordering: dataOrdering,
      }}
      contentProps={{ loading }}
      counterProps={{
        count,
        items: breadcrumbsItems,
      }}
      emptyStateProps={{
        EntityIcon: FileIcon,
        text: t('Modal.AttachFilesNoDataKey'),
      }}
      filePreviewProps={{
        files,
        initActive: activeFileIndex,
        onClose: handleCloseFilePreview,
      }}
      headerProps={{
        ...headerProps,
        text: headerProps?.text || 'Modal.AttachFilesTitleKey',
        onClose: handleCloseTableModal,
      }}
      isCloseConfirm={isCloseConfirm}
      isInitLoading={isInitLoading}
      loading={loading}
      notificationListProps={{
        ref: setNotificationListRef,
      }}
      notifications={notifications}
      paginationProps={{ pagination, onChange: setPagination }}
      qaName={QA_NAME_FILE_ATTACH_MODAL}
      selectAllPages={selectAllPages}
      selected={selected}
      selectionToolbarProps={{
        exclude,
        pagination: { count },
        selectAllPages,
        selected,
        onClose: handleUnselectAllPages,
      }}
      setCloseConfirm={setCloseConfirm}
      tableItems={files}
      tableProps={{
        TableRow: FileAttachedItemsModalTableRow,
        checkIsItemSelected,
        columns: ATTACH_FILES_COLUMNS_CONFIG,
        headerRowProps: {
          Actions: TableRowActions,
          actionsProps: {
            checked: isAllSelected,
            ordering: dataOrdering,
            selected: isRemoveSelection,
            onChange: handleSelectAll,
          },
          ordering: dataOrdering,
          resizable: false,
          withCellBorder: false,
          onSetOrdering: handleSetOrdering,
        },
        items: files,
        rowProps: {
          onClick: handleClickFile,
          onClickFolder: handleClickFolder,
          onSelect: handleSelect,
        },
      }}
      onApplyConfirmModal={onClose}
      onTableModalClose={handleCloseTableModal}
    />
  )
}

export default FileAttachItemsModal
